import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, MapPin, Phone, Mail, Facebook, Instagram, Twitter } from 'lucide-react';
import Navigation from '../components/Navigation';
import BookingModal from '../components/BookingModal';
import { images } from '../data/images';

function HomePage() {
  const { t } = useTranslation();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [currentReviewSet, setCurrentReviewSet] = useState(0);

  const reviewSets = [
    [1, 2, 3],
    [4, 5, 6]
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewSet((prev) => (prev + 1) % reviewSets.length);
    }, 20000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-bronze-50 via-brown-50 to-bronze-100">
      {/* Hero Section */}
      <header className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.common.hero}
            alt={t('hero.welcome')}
            className="w-full h-full object-cover brightness-110 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent"></div>
        </div>

        <Navigation />

        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-6 max-w-4xl mx-auto pb-20">
          <div className="space-y-6 animate-[fadeInUp_1s_ease-out]">
            <div className="flex justify-center mb-3">
              <img
                src={images.common.logo}
                alt="Il Galileo Rooms"
                className="w-full max-w-xs md:max-w-sm lg:max-w-md h-auto object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.8)] brightness-110"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md mx-auto px-8">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="bg-bronze-800/90 hover:bg-bronze-900 text-white px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {t('hero.book')}
              </button>

              <Link
                to="/availability"
                className="bg-white/95 text-brown-800 hover:bg-white px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 justify-center shadow-lg hover:shadow-xl"
              >
                {t('home.checkAvailability')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Rooms Section with Auto-Scrolling Carousel */}
      <section id="rooms" className="py-20 bg-bronze-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-stone-900 mb-3">{t('rooms.title')}</h2>
            <p className="text-stone-600 mb-8">{t('home.discoverRooms')}</p>
          </div>
        </div>

        <div className="relative mb-12 overflow-x-auto overflow-y-hidden scrollbar-hide scroll-smooth">
          <div className="flex gap-4 md:gap-8 animate-[scrollRTL_25s_linear_infinite] hover:pause px-4">
            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.doubleGallery[0]}
                  alt={t('rooms.types.double')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.double')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 2 })}</p>
                </div>
              </div>
            </Link>

            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.tripleGallery[0]}
                  alt={t('rooms.types.triple')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.triple')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 3 })}</p>
                </div>
              </div>
            </Link>

            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.quadGallery[0]}
                  alt={t('rooms.types.quadruple')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.quadruple')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 4 })}</p>
                </div>
              </div>
            </Link>

            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.familyGallery[0]}
                  alt={t('rooms.types.family')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.family')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 5 })}</p>
                </div>
              </div>
            </Link>

            {/* Duplicate for seamless loop */}
            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.doubleGallery[0]}
                  alt={t('rooms.types.double')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.double')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 2 })}</p>
                </div>
              </div>
            </Link>

            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.tripleGallery[0]}
                  alt={t('rooms.types.triple')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.triple')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 3 })}</p>
                </div>
              </div>
            </Link>

            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.quadGallery[0]}
                  alt={t('rooms.types.quadruple')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.quadruple')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 4 })}</p>
                </div>
              </div>
            </Link>

            <Link to="/rooms" className="group relative flex-shrink-0 w-72 sm:w-80 md:w-96 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="relative h-80 overflow-hidden">
                <img
                  src={images.rooms.standard.familyGallery[0]}
                  alt={t('rooms.types.family')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-2">{t('rooms.types.family')}</h3>
                  <p className="text-white/90 text-sm">{t('home.upToGuests', { count: 5 })}</p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <Link
              to="/rooms"
              className="inline-block bg-gradient-to-r from-bronze-700 to-bronze-800 hover:from-bronze-800 hover:to-bronze-900 text-white px-8 py-4 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {t('home.viewAllRoomDetails')}
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20 animate-fadeInUp">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-4">{t('home.amenitiesTitle')}</h2>
            <p className="text-lg text-stone-500 max-w-2xl mx-auto">{t('home.amenitiesSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative bg-gradient-to-br from-stone-50 to-stone-100 rounded-3xl p-10 hover:shadow-xl transition-all duration-500">
              <div className="space-y-4">
                <h3 className="text-2xl font-serif font-semibold text-stone-900">{t('home.sharedKitchen')}</h3>
                <p className="text-stone-600 leading-relaxed">{t('home.sharedKitchenDesc')}</p>
              </div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-bronze-600/10 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-stone-50 to-stone-100 rounded-3xl p-10 hover:shadow-xl transition-all duration-500">
              <div className="space-y-4">
                <h3 className="text-2xl font-serif font-semibold text-stone-900">{t('home.dailyHousekeeping')}</h3>
                <p className="text-stone-600 leading-relaxed">{t('home.dailyHousekeepingDesc')}</p>
              </div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-bronze-600/10 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-stone-50 to-stone-100 rounded-3xl p-10 hover:shadow-xl transition-all duration-500">
              <div className="space-y-4">
                <h3 className="text-2xl font-serif font-semibold text-stone-900">{t('home.diningOptions')}</h3>
                <p className="text-stone-600 leading-relaxed">{t('home.diningOptionsDesc')}</p>
              </div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-bronze-600/10 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Guest Reviews Section */}
      <section className="py-20 px-6 bg-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-stone-900 mb-3">{t('reviews.title')}</h2>
            <p className="text-stone-600">{t('home.guestReviewsSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {reviewSets[currentReviewSet].map((reviewNum) => (
              <div key={reviewNum} className="bg-amber-50 rounded-lg p-8 shadow-md hover:shadow-xl transition-all duration-500 animate-[fadeIn_0.5s_ease-in] hover:scale-105 transform">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-stone-700 mb-4 italic">"{t(`reviews.review${reviewNum}.text`)}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-700 rounded-full flex items-center justify-center text-white font-semibold">
                    {t(`reviews.review${reviewNum}.initials`)}
                  </div>
                  <div>
                    <div className="font-semibold text-stone-900">{t(`reviews.review${reviewNum}.name`)}</div>
                    <div className="text-sm text-stone-600">{t(`reviews.review${reviewNum}.location`)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <h3 className="text-xl font-serif font-semibold text-white mb-3">Il Galileo Rooms</h3>
              <p className="text-stone-400 text-sm mb-2">{t('home.experienceComfort')}</p>
              <p className="text-stone-500 text-xs">{t('hero.airportDescription')}</p>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-3">{t('footer.quickLinks')}</h4>
              <ul className="space-y-1.5">
                <li>
                  <Link to="/" className="hover:text-amber-500 transition-colors text-sm">{t('nav.home')}</Link>
                </li>
                <li>
                  <Link to="/rooms" className="hover:text-amber-500 transition-colors text-sm">{t('nav.rooms')}</Link>
                </li>
                <li>
                  <Link to="/availability" className="hover:text-amber-500 transition-colors text-sm">{t('nav.availability')}</Link>
                </li>
                <li>
                  <a href="#contact" className="hover:text-amber-500 transition-colors text-sm">{t('nav.contact')}</a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-white mb-3">{t('footer.contact')}</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{t('footer.address')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <a href={`tel:${t('contact.phone')}`} className="hover:text-amber-500 transition-colors text-sm">
                    {t('contact.phone')}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <a href={`mailto:${t('contact.email')}`} className="hover:text-amber-500 transition-colors text-sm">
                    {t('contact.email')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-white mb-3">{t('footer.followUs')}</h4>
              <div className="flex gap-3">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-stone-800 hover:bg-amber-700 rounded-full flex items-center justify-center transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-stone-800 hover:bg-amber-700 rounded-full flex items-center justify-center transition-colors">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-stone-800 hover:bg-amber-700 rounded-full flex items-center justify-center transition-colors">
                  <Twitter className="w-4 h-4" />
                </a>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setIsBookingModalOpen(true)}
                  className="w-full bg-amber-700 hover:bg-amber-800 text-white px-5 py-2.5 rounded-md font-medium transition-colors duration-200 text-sm"
                >
                  {t('hero.book')}
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-stone-800 pt-4 text-center text-stone-500">
            <p className="text-sm">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        selectedDates={null}
      />
    </div>
  );
}

export default HomePage;